import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import StatusBadge from '../components/ui/StatusBadge';
import { useWAHA } from '../hooks/useWAHA';
import { useAppStore } from '../stores/appStore';
import type { WahaEndpoint, WebhookEvent } from '../types';

export default function Settings(): React.JSX.Element {
  const { sessions, wahaConfig, loading, saveConfig, createSession, deleteSession, syncFromWAHA } = useWAHA();
  const orgId = useAppStore(s => s.orgId);

  const [wahaUrl, setWahaUrl]           = useState<string>("http://localhost:3000");
  const [apiKey, setApiKey]             = useState<string>("");
  const [selectedEngine, setSelectedEngine] = useState<string>("NOWEB");
  const [webhookUrl, setWebhookUrl]     = useState<string>("https://digitix.app/api/webhook");
  const [connectStep, setConnectStep]   = useState<string | null>(null);
  const [newSessionName, setNewSessionName] = useState<string>("");
  const [connMethod, setConnMethod]     = useState<string>("qr");
  const [testResult, setTestResult]     = useState<string | null>(null);
  const [wahaVersion, setWahaVersion]   = useState<string | null>(null);
  const [liveSessionCount, setLiveSessionCount] = useState<number | null>(null);
  const [activeTab, setActiveTab]       = useState<string>("connection");
  const [windowStart, setWindowStart]   = useState<string>("09:00");
  const [windowEnd, setWindowEnd]       = useState<string>("18:00");
  const [msgPer10Min, setMsgPer10Min]   = useState<number>(20);
  const [limitsTimezone, setLimitsTimezone] = useState<string>("UTC");
  const [savingLimits, setSavingLimits] = useState<boolean>(false);
  const [webhookEvents, setWebhookEvents] = useState<WebhookEvent[]>([
    { event: "message",         desc: "New incoming message received",      active: true },
    { event: "message.ack",     desc: "Message delivery/read status",       active: true },
    { event: "message.any",     desc: "Any message event (sent, received)", active: false },
    { event: "session.status",  desc: "Session connection status change",   active: true },
    { event: "group.join",      desc: "Contact joined a group",             active: false },
    { event: "presence.update", desc: "Contact online/offline/typing",      active: true },
    { event: "poll.vote",       desc: "Vote received on a poll message",    active: false },
  ]);

  // Sync form state when config loads from Supabase
  useEffect(() => {
    if (wahaConfig) {
      setWahaUrl(wahaConfig.base_url);
      setApiKey(wahaConfig.api_key ?? '');
      setSelectedEngine(wahaConfig.engine);
      setWebhookUrl(wahaConfig.webhook_url);
      if (wahaConfig.webhook_events.length > 0) {
        setWebhookEvents(prev =>
          prev.map(w => ({ ...w, active: wahaConfig.webhook_events.includes(w.event) }))
        );
      }
      if (wahaConfig.send_limits) {
        setWindowStart(wahaConfig.send_limits.window_start ?? '09:00');
        setWindowEnd(wahaConfig.send_limits.window_end ?? '18:00');
        setMsgPer10Min(wahaConfig.send_limits.messages_per_10min ?? 20);
        setLimitsTimezone(wahaConfig.send_limits.timezone ?? 'UTC');
      }
    }
  }, [wahaConfig]);

  const wahaConnected = sessions.some(s => s.status === 'WORKING');

  const handleTestConnection = async () => {
    setTestResult("testing");
    try {
      // Fetch version info and sessions from the real WAHA API
      const headers: Record<string, string> = {};
      if (apiKey) headers['X-Api-Key'] = apiKey;

      const [versionRes, liveSessions] = await Promise.all([
        fetch(`${wahaUrl}/api/version`, { headers }).then(r => r.ok ? r.json() : null).catch(() => null),
        syncFromWAHA(wahaUrl, apiKey || null),
      ]);

      setWahaVersion(versionRes?.version ?? null);
      setLiveSessionCount(liveSessions.length);
      setTestResult("success");
    } catch (err) {
      setTestResult("error");
      toast.error(err instanceof Error ? err.message : 'Failed to connect to WAHA');
    }
  };

  const handleSaveConfig = async () => {
    await saveConfig({
      org_id: orgId!,
      base_url: wahaUrl,
      api_key: apiKey || null,
      engine: selectedEngine as 'NOWEB' | 'WEBJS' | 'GOWS',
      webhook_url: webhookUrl,
      webhook_events: webhookEvents.filter(w => w.active).map(w => w.event),
    });
  };

  const handleStartSession = async () => {
    setConnectStep("qr");
    await createSession({
      session_name: newSessionName || 'new-session',
      status: 'STARTING',
      engine: selectedEngine as 'NOWEB' | 'WEBJS' | 'GOWS',
      waha_instance_url: wahaUrl,
      phone_number: null,
      push_name: null,
    });
    setConnectStep("connected");
  };

  const handleSaveLimits = async () => {
    setSavingLimits(true);
    try {
      await saveConfig({
        org_id: orgId!,
        base_url: wahaUrl,
        api_key: apiKey || null,
        engine: selectedEngine as 'NOWEB' | 'WEBJS' | 'GOWS',
        webhook_url: webhookUrl,
        webhook_events: webhookEvents.filter(w => w.active).map(w => w.event),
        send_limits: {
          window_start: windowStart,
          window_end: windowEnd,
          messages_per_10min: msgPer10Min,
          timezone: limitsTimezone,
        },
      });
    } finally {
      setSavingLimits(false);
    }
  };

  const tabs = [
    { id: "connection", label: "WAHA Connection",   icon: "🔌" },
    { id: "sessions",   label: "WhatsApp Sessions", icon: "📱" },
    { id: "limits",     label: "Sending Limits",    icon: "⏱️" },
    { id: "webhooks",   label: "Webhooks",           icon: "🔗" },
    { id: "api",        label: "API Reference",      icon: "📖" },
  ];

  const FakeQR = (): React.JSX.Element => (
    <div className="w-[200px] h-[200px] bg-white rounded-xl p-3 flex items-center justify-center relative">
      <svg viewBox="0 0 100 100" width="176" height="176">
        {Array.from({ length: 20 }).map((_, row) =>
          Array.from({ length: 20 }).map((_, col) => {
            const filled = ((row * 7 + col * 13 + row * col) % 3) !== 0;
            const isCorner =
              (row < 5 && col < 5) ||
              (row < 5 && col > 14) ||
              (row > 14 && col < 5);
            return (filled || isCorner) ? (
              <rect key={`${row}-${col}`} x={col * 5} y={row * 5} width={5} height={5} fill="#000" rx={0.5} />
            ) : null;
          })
        )}
      </svg>
      <div className="absolute w-8 h-8 bg-dx-accent rounded-[6px] flex items-center justify-center text-base">
        ⚡
      </div>
    </div>
  );

  const wahaEndpoints: WahaEndpoint[] = [
    { method: "POST",   path: "/api/sessions",                desc: "Create a new WhatsApp session" },
    { method: "GET",    path: "/api/sessions",                desc: "List all sessions and status" },
    { method: "POST",   path: "/api/sendText",                desc: "Send a text message" },
    { method: "POST",   path: "/api/sendImage",               desc: "Send an image with caption" },
    { method: "POST",   path: "/api/sendFile",                desc: "Send a document/file" },
    { method: "GET",    path: "/api/contacts/all/{session}",  desc: "Get all contacts" },
    { method: "GET",    path: "/api/messages",                desc: "Fetch message history" },
    { method: "GET",    path: "/api/{session}/chats",         desc: "Get all chats for session" },
    { method: "PUT",    path: "/api/sessions/{session}",      desc: "Update session config" },
    { method: "DELETE", path: "/api/sessions/{session}",      desc: "Delete/stop a session" },
  ];

  const methodColors: Record<string, string> = {
    GET:    "bg-[rgba(77,166,255,0.12)] text-dx-info",
    POST:   "bg-[rgba(37,211,102,0.12)] text-dx-accent",
    PUT:    "bg-[rgba(255,181,71,0.12)] text-dx-warning",
    DELETE: "bg-[rgba(255,77,106,0.12)] text-dx-danger",
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-dx-text m-0">Settings</h2>
          <p className="text-[13px] text-dx-muted mt-1 mb-0">WAHA integration &amp; WhatsApp session management</p>
        </div>
        <div
          className="flex items-center gap-1.5 px-3.5 py-2 rounded-[10px]"
          style={{
            background: wahaConnected ? "rgba(37,211,102,0.1)" : "rgba(255,77,106,0.1)",
            border: `1px solid ${wahaConnected ? "rgba(37,211,102,0.3)" : "rgba(255,77,106,0.3)"}`,
          }}
        >
          <span
            className="w-2 h-2 rounded-full"
            style={{
              background: wahaConnected ? "#25D366" : "#FF4D6A",
              boxShadow: `0 0 8px ${wahaConnected ? "#25D366" : "#FF4D6A"}`,
            }}
          />
          <span
            className="text-xs font-semibold"
            style={{ color: wahaConnected ? "#25D366" : "#FF4D6A" }}
          >
            WAHA {wahaConnected ? "Connected" : "Disconnected"}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-dx-surface rounded-xl p-1 border border-dx-border">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className="flex-1 px-4 py-2.5 rounded-[10px] border-none cursor-pointer text-[13px] flex items-center justify-center gap-1.5 transition-all duration-150"
            style={{
              background: activeTab === t.id ? "rgba(37,211,102,0.15)" : "transparent",
              color:      activeTab === t.id ? "#25D366" : "#8888A0",
              fontWeight: activeTab === t.id ? 600 : 400,
            }}
          >
            <span>{t.icon}</span> {t.label}
          </button>
        ))}
      </div>

      {/* Connection Tab */}
      {activeTab === "connection" && (
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-dx-surface border border-dx-border rounded-2xl p-6">
            <div className="flex items-center gap-2.5 mb-5">
              <div className="w-10 h-10 rounded-xl bg-[rgba(37,211,102,0.15)] flex items-center justify-center text-xl">
                🐳
              </div>
              <div>
                <h3 className="text-[15px] font-semibold text-dx-text m-0">WAHA Server</h3>
                <p className="text-xs text-dx-muted m-0">Docker container connection</p>
              </div>
            </div>
            <div className="flex flex-col gap-4">
              <div>
                <label className="text-xs font-semibold text-dx-muted mb-1.5 block">Server URL</label>
                <input
                  value={wahaUrl}
                  onChange={e => setWahaUrl(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-[10px] border border-dx-border bg-dx-bg text-dx-text text-[13px] outline-none font-mono box-border"
                  placeholder="http://localhost:3000"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-dx-muted mb-1.5 block">API Key (optional)</label>
                <input
                  value={apiKey}
                  onChange={e => setApiKey(e.target.value)}
                  type="password"
                  className="w-full px-3.5 py-2.5 rounded-[10px] border border-dx-border bg-dx-bg text-dx-text text-[13px] outline-none font-mono box-border"
                  placeholder="your-api-key-here"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-dx-muted mb-1.5 block">Engine</label>
                <div className="flex gap-2">
                  {(["NOWEB", "WEBJS", "GOWS"] as const).map(eng => (
                    <button
                      key={eng}
                      onClick={() => setSelectedEngine(eng)}
                      className="flex-1 py-2 px-3 rounded-lg text-xs font-semibold cursor-pointer font-mono transition-all duration-150"
                      style={{
                        border:     `1px solid ${selectedEngine === eng ? "rgba(37,211,102,0.5)" : "#1F1F2E"}`,
                        background: selectedEngine === eng ? "rgba(37,211,102,0.15)" : "#0A0A0F",
                        color:      selectedEngine === eng ? "#25D366" : "#8888A0",
                      }}
                    >
                      {eng}
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-dx-dim mt-1.5">
                  {selectedEngine === "NOWEB"
                    ? "⚡ Fastest — WebSocket-based, no browser, lowest RAM"
                    : selectedEngine === "WEBJS"
                    ? "🌐 Browser-based — Puppeteer, most compatible"
                    : "🦫 Go-based — newest engine, high performance"}
                </p>
              </div>
              <div className="flex gap-2.5">
                <button
                  onClick={handleTestConnection}
                  className="px-5 py-2.5 rounded-[10px] border-none text-[13px] font-semibold text-black cursor-pointer bg-[linear-gradient(135deg,#25D366,#1BA350)]"
                >
                  {testResult === "testing" ? "Testing..." : "Test & Sync"}
                </button>
                <button
                  onClick={handleSaveConfig}
                  disabled={loading}
                  className="bg-dx-surface border border-dx-border rounded-[10px] px-5 py-2.5 text-[13px] text-dx-text cursor-pointer disabled:opacity-50"
                >
                  {loading ? "Saving..." : "Save Config"}
                </button>
              </div>
              {testResult === "success" && (
                <div className="p-3 rounded-[10px] bg-[rgba(37,211,102,0.08)] border border-[rgba(37,211,102,0.125)] flex items-center gap-2">
                  <span className="text-base">✅</span>
                  <div>
                    <div className="text-[13px] font-semibold text-dx-accent">Connection successful!</div>
                    <div className="text-[11px] text-dx-muted">
                      {wahaVersion ? `WAHA ${wahaVersion}` : 'WAHA'} · Engine: {selectedEngine} · {liveSessionCount ?? sessions.length} sessions found
                    </div>
                  </div>
                </div>
              )}
              {testResult === "error" && (
                <div className="p-3 rounded-[10px] bg-[rgba(255,77,106,0.08)] border border-[rgba(255,77,106,0.125)] flex items-center gap-2">
                  <span className="text-base">❌</span>
                  <div className="text-[13px] font-semibold" style={{ color: '#FF4D6A' }}>Connection failed — check URL and API key</div>
                </div>
              )}
            </div>
          </div>

          {/* Quick Start */}
          <div className="bg-dx-surface border border-dx-border rounded-2xl p-6">
            <div className="flex items-center gap-2.5 mb-5">
              <div className="w-10 h-10 rounded-xl bg-[rgba(167,139,250,0.15)] flex items-center justify-center text-xl">
                📋
              </div>
              <div>
                <h3 className="text-[15px] font-semibold text-dx-text m-0">Quick Setup Guide</h3>
                <p className="text-xs text-dx-muted m-0">Get WAHA running in 60 seconds</p>
              </div>
            </div>
            <div className="flex flex-col gap-3.5">
              {[
                { step: 1, title: "Install Docker",         desc: "Make sure Docker is installed on your server",  done: true },
                { step: 2, title: "Run WAHA container",     code: "docker run -it -p 3000:3000 devlikeapro/waha",  done: true },
                { step: 3, title: "Enter server URL above", desc: "Default: http://localhost:3000",                 done: true },
                { step: 4, title: "Test connection",        desc: "Click 'Test Connection' to verify",              done: testResult === "success" },
                { step: 5, title: "Create a session",       desc: "Go to Sessions tab and scan QR code",            done: sessions.length > 0 },
              ].map(s => (
                <div key={s.step} className="flex gap-3 items-start">
                  <div
                    className="w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold"
                    style={{
                      background: s.done ? "#25D366" : "#0A0A0F",
                      border:     `2px solid ${s.done ? "#25D366" : "#1F1F2E"}`,
                      color:      s.done ? "#000" : "#55556A",
                    }}
                  >
                    {s.done ? "✓" : s.step}
                  </div>
                  <div className="flex-1">
                    <div className={`text-[13px] font-semibold ${s.done ? "text-dx-text" : "text-dx-muted"}`}>
                      {s.title}
                    </div>
                    {'desc' in s && s.desc && <div className="text-xs text-dx-dim mt-0.5">{s.desc}</div>}
                    {'code' in s && s.code && (
                      <div className="mt-1.5 px-3 py-2 rounded-lg bg-dx-bg border border-dx-border font-mono text-[11px] text-dx-accent">
                        $ {s.code}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Sessions Tab */}
      {activeTab === "sessions" && (
        <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))" }}>
          {loading && sessions.length === 0 ? (
            <div className="flex items-center justify-center h-32">
              <div className="w-6 h-6 border-2 border-dx-accent border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            sessions.map(s => (
              <div
                key={s.id}
                className="bg-dx-surface border border-dx-border rounded-2xl p-5 relative overflow-hidden"
              >
                <div
                  className="absolute top-0 left-0 right-0 h-0.5"
                  style={{ background: s.status === "WORKING" ? "#25D366" : "#FF4D6A" }}
                />
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-2.5">
                    <div className="w-11 h-11 rounded-xl bg-[rgba(37,211,102,0.15)] flex items-center justify-center text-[22px]">
                      📱
                    </div>
                    <div>
                      <div className="text-sm font-bold text-dx-text font-mono">{s.session_name}</div>
                      <div className="text-xs text-dx-muted">{s.push_name ?? '—'}</div>
                    </div>
                  </div>
                  <StatusBadge status={s.status === "WORKING" ? "active" : "paused"} />
                </div>
                <div className="grid grid-cols-2 gap-2.5 mb-4">
                  <div className="px-3 py-2 rounded-lg bg-dx-bg">
                    <div className="text-[11px] text-dx-dim">Phone</div>
                    <div className="text-[13px] font-semibold text-dx-text font-mono">{s.phone_number ?? '—'}</div>
                  </div>
                  <div className="px-3 py-2 rounded-lg bg-dx-bg">
                    <div className="text-[11px] text-dx-dim">Engine</div>
                    <div className="text-[13px] font-semibold text-dx-text font-mono">{s.engine}</div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button className="flex-1 py-2 px-3 text-xs bg-dx-surface border border-dx-border rounded-[10px] text-dx-text cursor-pointer">
                    Restart
                  </button>
                  <button className="flex-1 py-2 px-3 text-xs bg-dx-surface border border-dx-border rounded-[10px] text-dx-text cursor-pointer">
                    Screenshot
                  </button>
                  <button
                    onClick={() => deleteSession(s.id)}
                    className="py-2 px-3 text-xs bg-dx-surface rounded-[10px] cursor-pointer text-dx-danger border border-[rgba(255,77,106,0.3)]"
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))
          )}

          {/* Add New Session */}
          <div className="bg-dx-surface border-2 border-dashed border-dx-border rounded-2xl p-5 flex flex-col justify-center min-h-[260px]">
            {connectStep === null && (
              <div className="flex flex-col items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-[rgba(37,211,102,0.15)] flex items-center justify-center text-[28px]">
                  +
                </div>
                <div className="text-center">
                  <div className="text-[15px] font-semibold text-dx-text mb-1">Add WhatsApp Session</div>
                  <div className="text-xs text-dx-muted">Connect a new phone number</div>
                </div>
                <button
                  onClick={() => setConnectStep("config")}
                  className="px-5 py-2.5 rounded-[10px] border-none text-[13px] font-semibold text-black cursor-pointer bg-[linear-gradient(135deg,#25D366,#1BA350)]"
                >
                  Connect Number
                </button>
              </div>
            )}
            {connectStep === "config" && (
              <div className="flex flex-col gap-3.5">
                <h4 className="text-sm font-semibold text-dx-text m-0">New Session</h4>
                <div>
                  <label className="text-xs font-semibold text-dx-muted mb-1.5 block">Session Name</label>
                  <input
                    value={newSessionName}
                    onChange={e => setNewSessionName(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-[10px] border border-dx-border bg-dx-bg text-dx-text text-[13px] outline-none font-mono box-border"
                    placeholder="e.g. sales-team-3"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-dx-muted mb-1.5 block">Auth Method</label>
                  <div className="flex gap-2">
                    {[{ id: "qr", label: "📷 QR Code" }, { id: "pairing", label: "🔢 Pairing Code" }].map(m => (
                      <button
                        key={m.id}
                        onClick={() => setConnMethod(m.id)}
                        className="flex-1 py-2 rounded-lg text-xs font-semibold cursor-pointer transition-all duration-150"
                        style={{
                          background: connMethod === m.id ? "rgba(37,211,102,0.15)" : "#0A0A0F",
                          border:     `1px solid ${connMethod === m.id ? "rgba(37,211,102,0.5)" : "#1F1F2E"}`,
                          color:      connMethod === m.id ? "#25D366" : "#8888A0",
                        }}
                      >
                        {m.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleStartSession}
                    className="flex-1 px-5 py-2.5 rounded-[10px] border-none text-[13px] font-semibold text-black cursor-pointer bg-[linear-gradient(135deg,#25D366,#1BA350)]"
                  >
                    Start Session
                  </button>
                  <button
                    onClick={() => setConnectStep(null)}
                    className="bg-dx-surface border border-dx-border rounded-[10px] px-5 py-2.5 text-[13px] text-dx-text cursor-pointer"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
            {connectStep === "qr" && (
              <div className="flex flex-col items-center gap-4">
                <h4 className="text-sm font-semibold text-dx-text m-0">Scan QR Code</h4>
                <div className="relative">
                  <FakeQR />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div
                      className="w-[200px] h-[200px] rounded-xl border-[3px] border-dx-accent animate-pulse pointer-events-none"
                    />
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-dx-muted">Open WhatsApp → Linked Devices → Link a Device</div>
                  <div className="text-[11px] text-dx-dim mt-1">QR expires in 45s... auto-refreshing</div>
                </div>
                <button
                  onClick={() => setConnectStep(null)}
                  className="bg-dx-surface border border-dx-border rounded-[10px] px-4 py-2 text-xs text-dx-text cursor-pointer"
                >
                  Cancel
                </button>
              </div>
            )}
            {connectStep === "connected" && (
              <div className="flex flex-col items-center gap-3.5">
                <div className="w-14 h-14 rounded-full bg-[rgba(37,211,102,0.15)] flex items-center justify-center text-[28px] border-2 border-dx-accent">
                  ✅
                </div>
                <div className="text-center">
                  <div className="text-[15px] font-semibold text-dx-accent">Connected!</div>
                  <div className="text-xs text-dx-muted mt-1">
                    Session "{newSessionName || "new-session"}" is active
                  </div>
                </div>
                <button
                  onClick={() => { setConnectStep(null); setNewSessionName(""); }}
                  className="px-5 py-2.5 rounded-[10px] border-none text-[13px] font-semibold text-black cursor-pointer bg-[linear-gradient(135deg,#25D366,#1BA350)]"
                >
                  Done
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sending Limits Tab */}
      {activeTab === "limits" && (
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-dx-surface border border-dx-border rounded-2xl p-6">
            <div className="flex items-center gap-2.5 mb-5">
              <div className="w-10 h-10 rounded-xl bg-[rgba(255,181,71,0.15)] flex items-center justify-center text-xl">
                ⏱️
              </div>
              <div>
                <h3 className="text-[15px] font-semibold text-dx-text m-0">Sending Limits</h3>
                <p className="text-xs text-dx-muted m-0">Control outreach hours and message rate</p>
              </div>
            </div>
            <div className="flex flex-col gap-4">
              <div>
                <label className="text-xs font-semibold text-dx-muted mb-1.5 block">Timezone</label>
                <select
                  value={limitsTimezone}
                  onChange={e => setLimitsTimezone(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-[10px] border border-dx-border bg-dx-bg text-dx-text text-[13px] outline-none box-border"
                >
                  {[
                    "UTC",
                    "Europe/London",
                    "Europe/Berlin",
                    "Europe/Moscow",
                    "Asia/Baku",
                    "Asia/Dubai",
                    "Asia/Kolkata",
                    "Asia/Singapore",
                    "America/New_York",
                    "America/Chicago",
                    "America/Los_Angeles",
                  ].map(tz => (
                    <option key={tz} value={tz}>{tz}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-dx-muted mb-1.5 block">Working Hours</label>
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <label className="text-[11px] text-dx-dim mb-1 block">From</label>
                    <input
                      type="time"
                      value={windowStart}
                      onChange={e => setWindowStart(e.target.value)}
                      className="w-full px-3.5 py-2.5 rounded-[10px] border border-dx-border bg-dx-bg text-dx-text text-[13px] outline-none font-mono box-border"
                    />
                  </div>
                  <div className="text-dx-muted text-sm mt-4">→</div>
                  <div className="flex-1">
                    <label className="text-[11px] text-dx-dim mb-1 block">To</label>
                    <input
                      type="time"
                      value={windowEnd}
                      onChange={e => setWindowEnd(e.target.value)}
                      className="w-full px-3.5 py-2.5 rounded-[10px] border border-dx-border bg-dx-bg text-dx-text text-[13px] outline-none font-mono box-border"
                    />
                  </div>
                </div>
                <p className="text-[11px] text-dx-dim mt-1.5">
                  Campaign messages will only be sent during these hours in the selected timezone
                </p>
              </div>
              <div>
                <label className="text-xs font-semibold text-dx-muted mb-1.5 block">
                  Rate Limit — messages per 10 minutes
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min={1}
                    max={100}
                    value={msgPer10Min}
                    onChange={e => setMsgPer10Min(Number(e.target.value))}
                    className="flex-1 accent-dx-accent"
                  />
                  <div
                    className="w-14 text-center px-2 py-1.5 rounded-lg bg-dx-bg border border-dx-border font-mono text-[13px] font-bold"
                    style={{ color: msgPer10Min > 50 ? '#FF4D6A' : msgPer10Min > 20 ? '#FFB547' : '#25D366' }}
                  >
                    {msgPer10Min}
                  </div>
                </div>
                <p className="text-[11px] text-dx-dim mt-1">
                  {msgPer10Min <= 10
                    ? '✅ Very safe — minimal ban risk'
                    : msgPer10Min <= 20
                    ? '✅ Safe — recommended for new numbers'
                    : msgPer10Min <= 40
                    ? '⚠️ Moderate — use with warm accounts only'
                    : '❌ High — significant risk of WhatsApp ban'}
                </p>
              </div>
              <button
                onClick={handleSaveLimits}
                disabled={savingLimits}
                className="px-5 py-2.5 rounded-[10px] border-none text-[13px] font-semibold text-black cursor-pointer bg-[linear-gradient(135deg,#25D366,#1BA350)] disabled:opacity-50"
              >
                {savingLimits ? "Saving..." : "Save Sending Limits"}
              </button>
            </div>
          </div>

          <div className="bg-dx-surface border border-dx-border rounded-2xl p-6">
            <div className="flex items-center gap-2.5 mb-5">
              <div className="w-10 h-10 rounded-xl bg-[rgba(77,166,255,0.15)] flex items-center justify-center text-xl">
                📊
              </div>
              <div>
                <h3 className="text-[15px] font-semibold text-dx-text m-0">Current Limits</h3>
                <p className="text-xs text-dx-muted m-0">Active configuration summary</p>
              </div>
            </div>
            <div className="flex flex-col gap-3">
              <div className="px-4 py-3.5 rounded-xl bg-dx-bg border border-dx-border">
                <div className="text-[11px] text-dx-dim mb-1">Working Window</div>
                <div className="text-[15px] font-bold text-dx-text font-mono">
                  {windowStart} – {windowEnd}
                </div>
                <div className="text-[11px] text-dx-muted mt-0.5">{limitsTimezone}</div>
              </div>
              <div className="px-4 py-3.5 rounded-xl bg-dx-bg border border-dx-border">
                <div className="text-[11px] text-dx-dim mb-1">Rate Limit</div>
                <div
                  className="text-[15px] font-bold font-mono"
                  style={{ color: msgPer10Min > 50 ? '#FF4D6A' : msgPer10Min > 20 ? '#FFB547' : '#25D366' }}
                >
                  {msgPer10Min} msg / 10 min
                </div>
                <div className="text-[11px] text-dx-muted mt-0.5">
                  ≈ {msgPer10Min * 6} messages/hour · {msgPer10Min * 6 * (
                    (() => {
                      const [sh, sm] = windowStart.split(':').map(Number);
                      const [eh, em] = windowEnd.split(':').map(Number);
                      return Math.max(0, (eh * 60 + em - sh * 60 - sm) / 60);
                    })()
                  ).toFixed(0)} max/day
                </div>
              </div>
              <div className="px-4 py-3.5 rounded-xl bg-dx-bg border border-dx-border">
                <div className="text-[11px] text-dx-dim mb-2">Safety Tips</div>
                <div className="flex flex-col gap-1.5">
                  {[
                    { tip: "Start new numbers with 5–10 msg/10min", ok: msgPer10Min <= 10 },
                    { tip: "Warm up over 2–4 weeks before scaling", ok: true },
                    { tip: "Always add random delay between messages", ok: true },
                    { tip: "Keep rate under 20 msg/10min for safety", ok: msgPer10Min <= 20 },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-2 text-[11px] text-dx-muted">
                      <span style={{ color: item.ok ? '#25D366' : '#FF4D6A' }}>{item.ok ? '✓' : '✗'}</span>
                      {item.tip}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Webhooks Tab */}
      {activeTab === "webhooks" && (
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-dx-surface border border-dx-border rounded-2xl p-6">
            <h3 className="text-[15px] font-semibold text-dx-text mb-4 mt-0">Webhook Configuration</h3>
            <div className="flex flex-col gap-4">
              <div>
                <label className="text-xs font-semibold text-dx-muted mb-1.5 block">Webhook URL</label>
                <input
                  value={webhookUrl}
                  onChange={e => setWebhookUrl(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-[10px] border border-dx-border bg-dx-bg text-dx-text text-[13px] outline-none font-mono box-border"
                  placeholder="https://your-app.com/api/webhook"
                />
                <p className="text-[11px] text-dx-dim mt-1">
                  WAHA will POST event data to this URL in real-time
                </p>
              </div>
              <div>
                <label className="text-xs font-semibold text-dx-muted mb-1.5 block">Webhook Events</label>
                <div className="flex flex-col gap-1.5">
                  {webhookEvents.map(w => (
                    <div
                      key={w.event}
                      className="flex items-center justify-between px-3.5 py-2.5 rounded-[10px] bg-dx-bg border border-dx-border"
                    >
                      <div>
                        <div className="text-[13px] font-semibold text-dx-text font-mono">{w.event}</div>
                        <div className="text-[11px] text-dx-dim">{w.desc}</div>
                      </div>
                      <div
                        onClick={() => setWebhookEvents(prev =>
                          prev.map(e => e.event === w.event ? { ...e, active: !e.active } : e)
                        )}
                        className="w-10 h-[22px] rounded-[11px] cursor-pointer relative transition-colors duration-200"
                        style={{ background: w.active ? "#25D366" : "#1F1F2E" }}
                      >
                        <div
                          className="w-[18px] h-[18px] rounded-full bg-white absolute top-0.5 transition-all duration-200 shadow-[0_1px_3px_rgba(0,0,0,0.3)]"
                          style={{ left: w.active ? 20 : 2 }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <button
                onClick={handleSaveConfig}
                disabled={loading}
                className="px-5 py-2.5 rounded-[10px] border-none text-[13px] font-semibold text-black cursor-pointer bg-[linear-gradient(135deg,#25D366,#1BA350)] disabled:opacity-50"
              >
                {loading ? "Saving..." : "Save Webhook Config"}
              </button>
            </div>
          </div>

          <div className="bg-dx-surface border border-dx-border rounded-2xl p-6">
            <h3 className="text-[15px] font-semibold text-dx-text mb-4 mt-0">Payload Preview</h3>
            <p className="text-xs text-dx-muted mb-3">Example incoming message webhook:</p>
            <pre className="p-4 rounded-xl bg-dx-bg border border-dx-border font-mono text-[11px] text-dx-text overflow-auto leading-relaxed whitespace-pre-wrap">{`{
  "event": "message",
  "session": "sales-main",
  "payload": {
    "id": "ABCDEF123456",
    "timestamp": 1709913600,
    "from": "994501234567@c.us",
    "to": "15550123456@c.us",
    "body": "Yes, I'd like to know more!",
    "hasMedia": false,
    "fromMe": false,
    "_data": {
      "notifyName": "Sarah Chen"
    }
  },
  "engine": "NOWEB"
}`}</pre>
            <div className="mt-4">
              <h4 className="text-[13px] font-semibold text-dx-text mb-2.5 mt-0">How Digitix processes this:</h4>
              <div className="flex flex-col gap-2">
                {[
                  "1️⃣ Webhook received → parse contact & message",
                  "2️⃣ Route to assigned AI agent based on campaign",
                  "3️⃣ AI generates response → POST /api/sendText",
                  "4️⃣ Update conversation & analytics in real-time",
                ].map((step, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-dx-muted">{step}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* API Reference Tab */}
      {activeTab === "api" && (
        <div className="flex flex-col gap-4">
          <div className="bg-dx-surface border border-dx-border rounded-2xl p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-[15px] font-semibold text-dx-text m-0">WAHA API Endpoints</h3>
              <span className="text-xs text-dx-muted font-mono">Base: {wahaUrl}</span>
            </div>
            <div className="flex flex-col gap-1.5">
              {wahaEndpoints.map((ep, i) => (
                <div
                  key={i}
                  className="card-hover flex items-center gap-3.5 px-4 py-3 rounded-[10px] cursor-pointer bg-dx-bg"
                >
                  <span
                    className={`px-2.5 py-0.5 rounded-[6px] text-[11px] font-bold font-mono min-w-[56px] text-center ${methodColors[ep.method] ?? ""}`}
                  >
                    {ep.method}
                  </span>
                  <span className="text-[13px] font-semibold text-dx-text font-mono flex-1">{ep.path}</span>
                  <span className="text-xs text-dx-dim">{ep.desc}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-dx-surface border border-dx-border rounded-2xl p-6">
            <h3 className="text-[15px] font-semibold text-dx-text mb-4 mt-0">
              Digitix → WAHA Integration Example
            </h3>
            <pre className="p-4 rounded-xl bg-dx-bg border border-dx-border font-mono text-[11px] text-dx-text overflow-auto leading-relaxed whitespace-pre-wrap">{`// Send a cold outreach message via WAHA
async function sendColdMessage(session, phone, message) {
  const response = await fetch(\`\${WAHA_URL}/api/sendText\`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      chatId: \`\${phone}@c.us\`,
      text: message,
      session: session
    })
  });
  return response.json();
}

// Campaign engine - process contact list
async function runCampaign(campaign) {
  for (const contact of campaign.contacts) {
    const msg = campaign.template
      .replace('{{name}}', contact.firstName)
      .replace('{{company}}', contact.company);

    await sendColdMessage(campaign.session, contact.phone, msg);
    // Rate limit - random delay 30-90s
    await sleep(30000 + Math.random() * 60000);
  }
}

// Webhook handler - incoming messages
app.post('/api/webhook', async (req, res) => {
  const { event, payload, session } = req.body;
  if (event === 'message' && !payload.fromMe) {
    const aiResponse = await aiAgent.generate(
      payload.body,
      getConversationHistory(payload.from)
    );
    await sendColdMessage(
      session, payload.from.replace('@c.us',''), aiResponse
    );
  }
  res.sendStatus(200);
});`}</pre>
          </div>
        </div>
      )}
    </div>
  );
}
